// useTVScale — fits the v2 board to whatever viewport the TV's browser reports.
//
// The board is designed at 1920x1080. TVs don't reliably report that: an
// Android TV box at device-pixel-ratio 2 reports 960x540 CSS px, a 720p panel
// reports 1280x720, a 4K panel at DPR 1 reports 3840x2160. With fixed rem/px
// sizes the same markup then shows four rows and clips every name on one TV
// and looks tiny on another.
//
// Every Tailwind size utility (text-*, h-*, p-*, gap-*) is rem-based, so
// setting the root font-size to 16px × min(vw/1920, vh/1080) scales the whole
// board proportionally and the 1080p layout fits every screen. Scoped to the
// display screen: restored on unmount so the picker/setup pages are unaffected.
import { useEffect } from "react";

export const DESIGN_WIDTH = 1920;
export const DESIGN_HEIGHT = 1080;
const BASE_FONT_PX = 16;
// A very small dev window shouldn't collapse the board into illegible text.
const MIN_SCALE = 0.35;

export const tvScaleFor = (width: number, height: number): number =>
  Math.max(MIN_SCALE, Math.min(width / DESIGN_WIDTH, height / DESIGN_HEIGHT));

export const useTVScale = () => {
  useEffect(() => {
    const root = document.documentElement;
    const previous = root.style.fontSize;
    const apply = () => {
      const scale = tvScaleFor(window.innerWidth, window.innerHeight);
      root.style.fontSize = `${(BASE_FONT_PX * scale).toFixed(3)}px`;
    };
    apply();
    window.addEventListener("resize", apply);
    // Entering/leaving fullscreen changes the viewport without always firing
    // resize on TV browsers.
    document.addEventListener("fullscreenchange", apply);
    return () => {
      window.removeEventListener("resize", apply);
      document.removeEventListener("fullscreenchange", apply);
      root.style.fontSize = previous;
    };
  }, []);
};
